/**
 * PetaCMS admin-web entry point.
 * Phase 3 で本格実装。現在は最小構成のみ。
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { initPostHog, trackPageView } from './lib/posthog.js';
import { initSentry } from './lib/sentry.js';
import './styles/globals.css';
import { router } from './router.js';

// Sentry 初期化 (VITE_SENTRY_DSN が未設定なら noop)
initSentry();

// PostHog 初期化 (VITE_POSTHOG_KEY が未設定なら noop)
initPostHog();

// SPA ページビュー追跡
router.subscribe('onResolved', ({ toLocation }) => {
  trackPageView(toLocation.pathname);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx (client errors)
        if (error instanceof Error && 'status' in error) {
          const status = (error as unknown as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 3;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
